// pkg74 — engage flashes also report the module cascade ("…and N modules enabled", D-74d companion) — delivered 2026-07-13
// pkg67b — engaging a built-in area now reports its auto-seeded starter count ("Added Family Law — 12 common case types included"); creating a custom area whose name matches a built-in shows the doppelgänger message instead of minting a twin (D-67b2/b4) — delivered 2026-07-12
import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

// Practice Areas & Case Types — the firm's setup for the discovery / case-type
// hierarchy. Collapsible by design so it stays compact even with many areas:
//   • engaged practice areas are collapsed rows; expand one to manage its case
//     types (add / edit / delete the firm's own; built-ins shown read-only)
//   • "Add practice area" pulls from the catalog
//   • court is an optional free-text label used only on generated documents,
//     with the Tennessee courts offered as one-tap presets
// Owner-only (route is Gated('owner', ...) in App.js).

const META = 'Arial, sans-serif';
const COURT_PRESETS = ['Circuit', 'Chancery', 'Juvenile'];

export default function AdminPracticeAreasPage() {
  const [areas, setAreas]         = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [expanded, setExpanded]   = useState(() => new Set());
  const [picking, setPicking]     = useState(false);
  const [pickId, setPickId]       = useState('');
  const [newAreaName, setNewAreaName] = useState('');
  const [busyArea, setBusyArea]   = useState(false);

  function flash(msg) {
    setSuccess(msg); setError('');
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setSuccess(''), 3500);
  }

  const loadAll = useCallback(async () => {
    const [areaData, cts] = await Promise.all([
      adminApi.getFirmPracticeAreas(),
      adminApi.getFirmCaseTypes().catch(() => []),
    ]);
    setAreas(areaData.practice_areas || []);
    setCaseTypes(Array.isArray(cts) ? cts : []);
  }, []);

  useEffect(() => {
    loadAll().catch(err => setError(err.message)).finally(() => setLoading(false));
  }, [loadAll]);

  const engaged   = areas.filter(a => a.selected).sort((a, b) => a.sort_order - b.sort_order);
  const available = areas.filter(a => !a.selected).sort((a, b) => a.sort_order - b.sort_order);
  const engagedIds = () => areas.filter(a => a.selected).map(a => a.id);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function addArea() {
    const name = newAreaName.trim();
    if (!pickId && !name) return;
    setBusyArea(true); setError('');
    try {
      if (name) {
        const created = await adminApi.createFirmPracticeArea(name);
        await loadAll();
        if (created?.id) setExpanded(prev => new Set(prev).add(created.id));
        // pkg67b (D-67b4) — the backend refuses to mint a custom twin of a
        // built-in area; it engages the built-in (+seeds starters) and says so.
        if (created?.matched_builtin) {
          // pkg74 — mention the module cascade when it did something.
          const mods = created.modules_enabled
            ? ` ${created.modules_enabled} module${created.modules_enabled === 1 ? '' : 's'} enabled.`
            : '';
          flash(created.seeded_count
            ? `${created.label} is already built in — enabled it for you, with ${created.seeded_count} starter case types.${mods}`
            : `${created.label} is already built in — enabled it for you.${mods}`);
        } else {
          flash('Practice area created.');
        }
      } else {
        // pkg67b (D-67b2) — the PUT response reports starters auto-seeded for
        // the newly-engaged built-in area, so the flash can say the area
        // arrived furnished rather than leaving the owner to discover it.
        const picked = areas.find(a => a.id === pickId);
        const resp = await adminApi.setFirmPracticeAreas([...engagedIds(), pickId]);
        await loadAll();
        setExpanded(prev => new Set(prev).add(pickId));
        const n = (resp && resp.seeded && picked) ? (resp.seeded[picked.key] || 0) : 0;
        // pkg74 — mention the module cascade when it did something.
        const mods = resp?.modules_enabled
          ? ` ${resp.modules_enabled} module${resp.modules_enabled === 1 ? '' : 's'} enabled.`
          : '';
        flash(n
          ? `Added ${picked.label} — ${n} common case types included. Rename or delete any of them.${mods}`
          : `Practice area added.${mods}`);
      }
      setPicking(false); setPickId(''); setNewAreaName('');
    } catch (err) { setError(err.message); }
    finally { setBusyArea(false); }
  }

  async function removeArea(area) {
    const ctCount = caseTypes.filter(ct => ct.practice_area === area.key && ct.is_custom).length;
    if (ctCount > 0) {
      setError(`Remove the ${ctCount} case type${ctCount === 1 ? '' : 's'} under "${area.label}" before removing the area.`);
      return;
    }
    const verb = area.is_custom ? 'Delete' : 'Remove';
    if (!window.confirm(`${verb} "${area.label}"?`)) return;
    setBusyArea(true); setError('');
    try {
      if (area.is_custom) {
        await adminApi.deleteFirmPracticeArea(area.id);
      } else {
        await adminApi.setFirmPracticeAreas(engagedIds().filter(id => id !== area.id));
      }
      await loadAll();
      flash(area.is_custom ? 'Practice area deleted.' : 'Practice area removed.');
    } catch (err) { setError(err.message); }
    finally { setBusyArea(false); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: META, color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Practice Areas &amp; Case Types</h1>
      <p className="page-subtitle">
        Add the areas of law your firm handles, then define your case types under each one. Case
        types are how you organize matters and how the discovery generator picks the right standard
        set. Click an area to expand it. Only owners can edit this.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {engaged.length === 0 && (
        <div className="card" style={{ fontFamily: META, fontSize: 13, color: 'var(--light)' }}>
          You haven&apos;t added any practice areas yet. Add one below to get started.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {engaged.map(area => (
          <AreaAccordionItem
            key={area.id}
            area={area}
            open={expanded.has(area.id)}
            onToggle={() => toggleExpand(area.id)}
            caseTypes={caseTypes.filter(ct => ct.practice_area === area.key)}
            onChanged={loadAll}
            onRemove={() => removeArea(area)}
            onError={setError}
            onFlash={flash}
            busyArea={busyArea}
          />
        ))}
      </div>

      {/* Add practice area */}
      <div style={{ marginTop: 16 }}>
        {picking ? (
          <div className="card">
            {available.length > 0 && (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: META, fontSize: 12 }}>Add from the standard list</label>
                <select
                  value={pickId}
                  onChange={e => { setPickId(e.target.value); if (e.target.value) setNewAreaName(''); }}
                  style={inputStyle}
                >
                  <option value="">Select an area…</option>
                  {available.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontFamily: META, fontSize: 12 }}>
                {available.length > 0 ? 'Or create your own area' : 'Create your own area'}
              </label>
              <input
                value={newAreaName}
                onChange={e => { setNewAreaName(e.target.value); if (e.target.value) setPickId(''); }}
                placeholder="e.g. Aviation Law"
                style={inputStyle}
              />
            </div>
            <div className="btn-row" style={{ marginTop: 0 }}>
              <button className="btn btn-primary btn-sm" onClick={addArea} disabled={busyArea || (!pickId && !newAreaName.trim())}>
                {busyArea ? <><span className="spinner" />Adding...</> : 'Add'}
              </button>
              <button className="btn btn-sm" onClick={() => { setPicking(false); setPickId(''); setNewAreaName(''); }} disabled={busyArea}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className="btn" onClick={() => { setPicking(true); setError(''); }}>
            + Add practice area
          </button>
        )}
      </div>
    </div>
  );
}

// ── One engaged practice area: collapsible header + case-type management ─────
function AreaAccordionItem({ area, open, onToggle, caseTypes, onChanged, onRemove, onError, onFlash, busyArea }) {
  const [adding, setAdding] = useState(false);
  const [loadingStarters, setLoadingStarters] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(area.label);
  const customCount = caseTypes.filter(ct => ct.is_custom).length;
  const linkStyle = {
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    fontFamily: META, fontSize: 12, color: 'var(--light)', textDecoration: 'underline',
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px', background: 'transparent', border: 'none',
          cursor: 'pointer', fontFamily: META, textAlign: 'left',
        }}
      >
        <span style={{
          display: 'inline-block', width: 16, color: 'var(--light)', fontSize: 12,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
        }}>▶</span>
        <span style={{ fontSize: 15, color: 'var(--dark)', fontWeight: 600 }}>{area.label}</span>
        {area.is_custom && <Badge tone="muted" style={{ marginLeft: 6 }}>Custom</Badge>}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--light)' }}>
          {caseTypes.length === 0 ? 'No case types' : `${caseTypes.length} case type${caseTypes.length === 1 ? '' : 's'}`}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {caseTypes.length === 0 && !adding && (
              <div>
                <p style={{ fontFamily: META, fontSize: 13, color: 'var(--light)', margin: '0 0 8px' }}>
                  {area.is_custom
                    ? 'No case types yet — add your own below.'
                    : 'No case types yet — load a starter set to get going, then edit or delete any of them.'}
                </p>
                {!area.is_custom && (
                  <button
                    className="btn btn-sm"
                    disabled={loadingStarters}
                    onClick={async () => {
                      setLoadingStarters(true); onError('');
                      try {
                        const r = await adminApi.loadStarterCaseTypes({ practice_area: area.key });
                        onChanged();
                        onFlash(r.count
                          ? `Loaded ${r.count} starter case type${r.count === 1 ? '' : 's'}. Rename or delete any of them freely.`
                          : 'No new starter types to add.');
                      } catch (err) { onError(err.message); }
                      finally { setLoadingStarters(false); }
                    }}
                  >
                    {loadingStarters ? <><span className="spinner" />Loading...</> : 'Load starter case types'}
                  </button>
                )}
              </div>
            )}
            {caseTypes.map(ct => (
              <CaseTypeRow key={ct.id} ct={ct} onChanged={onChanged} onError={onError} onFlash={onFlash} />
            ))}
          </div>

          {adding ? (
            <CaseTypeEditor
              areaKey={area.key}
              onCancel={() => setAdding(false)}
              onSaved={() => { setAdding(false); onChanged(); onFlash('Case type added.'); }}
              onError={onError}
            />
          ) : (
            <button className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => { setAdding(true); onError(''); }}>
              + Add case type
            </button>
          )}

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {area.is_custom && (renaming ? (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
                />
                <button
                  className="btn btn-sm"
                  disabled={busyArea}
                  onClick={async () => {
                    const v = renameVal.trim();
                    if (!v) return;
                    onError('');
                    try {
                      await adminApi.renameFirmPracticeArea(area.id, v);
                      setRenaming(false);
                      onChanged();
                      onFlash('Practice area renamed.');
                    } catch (err) { onError(err.message); }
                  }}
                >Save</button>
                <button className="btn btn-sm" onClick={() => { setRenaming(false); setRenameVal(area.label); }}>Cancel</button>
              </span>
            ) : (
              <button onClick={() => { setRenaming(true); setRenameVal(area.label); onError(''); }} style={linkStyle}>
                Rename
              </button>
            ))}
            <button onClick={onRemove} disabled={busyArea} style={linkStyle}>
              {area.is_custom ? 'Delete this practice area' : 'Remove this practice area'}
            </button>
            {customCount > 0 && (
              <span style={{ fontFamily: META, fontSize: 12, color: 'var(--light)' }}>
                (remove its {customCount} case type{customCount === 1 ? '' : 's'} first)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── A single case type: read row + inline edit (custom only) ────────────────
function CaseTypeRow({ ct, onChanged, onError, onFlash }) {
  const [editing, setEditing] = useState(false);

  async function handleDelete() {
    if (!window.confirm(`Delete case type "${ct.label}"? This cannot be undone.`)) return;
    onError('');
    try {
      await adminApi.deleteFirmCaseType(ct.id);
      onChanged();
      onFlash('Case type deleted.');
    } catch (err) { onError(err.message); }
  }

  if (editing) {
    return (
      <CaseTypeEditor
        existing={ct}
        onCancel={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChanged(); onFlash('Case type saved.'); }}
        onError={onError}
      />
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
      border: '1px solid var(--border)', borderRadius: 4, fontFamily: META,
    }}>
      <span style={{ fontSize: 14, color: 'var(--dark)', flex: 1 }}>
        {ct.label}
        {ct.court && <span style={{ color: 'var(--light)', fontSize: 12 }}> · {ct.court}</span>}
      </span>
      {ct.is_custom
        ? <Badge tone="dark">Custom</Badge>
        : <Badge tone="muted">Library</Badge>}
      <button className="btn btn-sm" onClick={() => { setEditing(true); onError(''); }}>Edit</button>
      {ct.is_custom && (
        <button className="btn btn-sm" onClick={handleDelete} style={{ color: '#b0261a', borderColor: '#e2b9b4' }}>Delete</button>
      )}
    </div>
  );
}

// ── Add / edit a custom case type (free-text court with presets) ────────────
function CaseTypeEditor({ existing, areaKey, onCancel, onSaved, onError }) {
  const [label, setLabel] = useState(existing?.label || '');
  const [court, setCourt] = useState(existing?.court || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = label.trim();
    if (!v) { onError('Case type name is required.'); return; }
    setSaving(true); onError('');
    try {
      const courtVal = court.trim() || null;
      if (existing) {
        await adminApi.updateFirmCaseType(existing.id, { label: v, court: courtVal });
      } else {
        await adminApi.createFirmCaseType({ label: v, practice_area: areaKey, court: courtVal });
      }
      onSaved();
    } catch (err) { onError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{
      marginTop: 12, padding: 12, border: '1px solid var(--border)',
      borderLeft: '3px solid var(--dark)', borderRadius: 4,
    }}>
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label style={{ fontFamily: META, fontSize: 12 }}>Case type name</label>
        <input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="e.g. Divorce — with children"
          style={inputStyle}
          autoFocus
        />
      </div>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label style={{ fontFamily: META, fontSize: 12 }}>Court (optional)</label>
        <input
          value={court}
          onChange={e => setCourt(e.target.value)}
          placeholder="Leave blank if not applicable"
          style={inputStyle}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {COURT_PRESETS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCourt(c)}
              style={{
                fontFamily: META, fontSize: 12, padding: '3px 10px', borderRadius: 12,
                cursor: 'pointer',
                border: '1px solid ' + (court === c ? 'var(--dark)' : 'var(--border)'),
                background: court === c ? 'var(--dark)' : 'var(--white)',
                color: court === c ? 'var(--white)' : 'var(--dark)',
              }}
            >
              {c}
            </button>
          ))}
          {court && (
            <button
              type="button"
              onClick={() => setCourt('')}
              style={{
                fontFamily: META, fontSize: 12, padding: '3px 10px', borderRadius: 12,
                cursor: 'pointer', border: '1px solid var(--border)',
                background: 'var(--white)', color: 'var(--light)',
              }}
            >
              Clear
            </button>
          )}
        </div>
        <p style={{ fontFamily: META, fontSize: 11, color: 'var(--light)', margin: '8px 0 0' }}>
          Only used to label generated documents (e.g. the court name on a filing). Leave blank if it doesn&apos;t apply.
        </p>
      </div>
      <div className="btn-row" style={{ marginTop: 0 }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <><span className="spinner" />Saving...</> : (existing ? 'Save' : 'Add case type')}
        </button>
        <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function Badge({ children, tone = 'muted' }) {
  const tones = {
    dark:  { background: 'var(--dark)', color: 'var(--white)', border: '1px solid var(--dark)' },
    muted: { background: 'var(--bg)', color: 'var(--light)', border: '1px solid var(--border)' },
  };
  return (
    <span style={{
      display: 'inline-block', fontFamily: META, fontSize: 10, textTransform: 'uppercase',
      letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 3,
      ...(tones[tone] || tones.muted),
    }}>
      {children}
    </span>
  );
}

const inputStyle = {
  width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 14, padding: 9,
  border: '1px solid var(--border)', borderRadius: 3, boxSizing: 'border-box',
};
